import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { kaireraAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  ArrowRight, ArrowLeft, Loader2, CheckCircle, Brain, Sparkles,
  BarChart3, ChevronRight, Lock, Shield, Heart, Target, Users,
  AlertTriangle, Award, Zap, ArrowUpDown, Check, X, Share2,
  Download, Lightbulb, TrendingUp, ExternalLink, RotateCcw,
  Filter, Copy, Link2, User, Calendar, Clock
} from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Cell, Legend
} from 'recharts';

const BATTERY_ICONS = {
  ocean: Brain, eq: Heart, disc: Target, strengths: Zap,
  hogan: Shield, kfla: Award, stallers: AlertTriangle,
};

const BATTERY_COLORS = {
  ocean: '#3B82F6', eq: '#EC4899', disc: '#F5841F', strengths: '#16A34A',
  hogan: '#DC2626', kfla: '#8B5CF6', stallers: '#EAB308',
};

const BATTERY_NAMES = {
  ocean: 'Big Five (OCEAN) Personality',
  eq: 'Emotional Intelligence',
  disc: 'DISC Behavioral Profile',
  strengths: 'Strengths Orientation',
  hogan: 'Hogan Dark Side Derailers',
  kfla: 'Korn Ferry Leadership Factors',
  stallers: 'Career Stallers Quick Screen',
};

const BATTERY_CATEGORIES = {
  ocean: 'Personality', eq: 'Emotional Intelligence', disc: 'Behavioral',
  strengths: 'Performance', hogan: 'Risk Assessment', kfla: 'Leadership', stallers: 'Risk Assessment',
};

const CATEGORIES = ['All', 'Personality', 'Emotional Intelligence', 'Behavioral', 'Performance', 'Leadership', 'Risk Assessment'];

const COMING_SOON = [
  { name: 'Enneagram', desc: 'Deep motivational and relational patterns', icon: Users, color: '#0D9488' },
  { name: 'CliftonStrengths', desc: 'Gallup 34 talent themes assessment', icon: Award, color: '#16A34A' },
  { name: 'Culture Add', desc: 'Team culture alignment and fit analysis', icon: Heart, color: '#EC4899' },
];

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'assessments', label: 'My Assessments', icon: Brain },
  { id: 'profile', label: 'Who You Are', icon: User },
  { id: 'comparison', label: 'Peer Comparison', icon: Users },
];

// ---- MAIN EXPORT (wraps old page logic) ----
export default function KaireraAssessmentBatteriesPage() {
  const [batteries, setBatteries] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBattery, setActiveBattery] = useState(null);
  const [error, setError] = useState(null);
  const [isPro, setIsPro] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [battRes, featRes, resRes] = await Promise.allSettled([
        kaireraAPI.getBatteries(),
        import('../lib/api').then(m => m.subscriptionAPI.features()),
        kaireraAPI.getAssessmentResults(),
      ]);
      if (battRes.status === 'fulfilled') setBatteries(battRes.value.data.batteries);
      else if (battRes.reason?.response?.status === 404) setError('no_session');
      if (featRes.status === 'fulfilled') setIsPro(featRes.value.data.level >= 1);
      else setIsPro(false);
      if (resRes.status === 'fulfilled') setResults(resRes.value.data.results || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (activeBattery) {
    const BatteryFlow = React.lazy(() => import('./KaireraAssessmentBatteriesPage').then(m => ({ default: m.BatteryFlowComponent || (() => null) })));
    return null; // Handled by parent route
  }

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0D9488' }} />
    </div>
  );

  if (error === 'no_session') return (
    <div className="max-w-lg mx-auto mt-20 text-center">
      <Brain className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--t-text-muted)' }} />
      <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--t-text-heading)' }}>Start Your Discovery First</h2>
      <p className="text-sm mb-4" style={{ color: 'var(--t-text-muted)' }}>Complete the Career Discovery to unlock Assessment Batteries.</p>
      <Button onClick={() => navigate('/career-discovery')} style={{ background: '#0D9488', color: '#fff' }}>
        Start Discovery
      </Button>
    </div>
  );

  const resultMap = {};
  results.forEach(r => { resultMap[r.battery_id] = r; });
  const completedCount = batteries.filter(b => b.is_complete).length;

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="assessment-page">
      {/* Header */}
      <div>
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#0D9488' }}>
          Career Intelligence Engine
        </p>
        <h1 className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>Assessment Center</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b pb-0" style={{ borderColor: 'var(--t-border)' }}>
        {TABS.map(t => {
          const TIcon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold -mb-px transition-all"
              style={{
                borderBottom: isActive ? '2px solid #0D9488' : '2px solid transparent',
                color: isActive ? '#0D9488' : 'var(--t-text-muted)',
              }}
              data-testid={`assess-tab-${t.id}`}>
              <TIcon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
          {activeTab === 'overview' && (
            <OverviewTab batteries={batteries} results={results} resultMap={resultMap}
              completedCount={completedCount} onStart={id => setActiveBattery(id)} />
          )}
          {activeTab === 'assessments' && (
            <AssessmentsTab batteries={batteries} results={results} resultMap={resultMap}
              onStart={id => setActiveBattery(id)} onRetake={async (id) => {
                // Reset and restart
                try {
                  await kaireraAPI.retakeBattery(id);
                  loadData();
                } catch { setActiveBattery(id); }
              }} />
          )}
          {activeTab === 'profile' && (
            <ProfileTab results={results} completedCount={completedCount} totalCount={batteries.length} />
          )}
          {activeTab === 'comparison' && (
            <ComparisonTab completedCount={completedCount} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ---- OVERVIEW TAB ----
function OverviewTab({ batteries, results, resultMap, completedCount, onStart }) {
  const [showShareCard, setShowShareCard] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [includeLink, setIncludeLink] = useState(false);
  const [userName, setUserName] = useState('Career Professional');
  const shareRef = useRef(null);
  const pct = Math.round((completedCount / batteries.length) * 100);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.name) setUserName(payload.name);
      }
    } catch {}
  }, []);

  const generateShareLink = async () => {
    try {
      const res = await kaireraAPI.createAssessmentShareLink();
      setShareLink(res.data.share_id);
    } catch {}
  };

  const downloadCard = async () => {
    if (!shareRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(shareRef.current, { scale: 3, useCORS: true, backgroundColor: '#0B1120' });
      const link = document.createElement('a');
      link.download = `kairera-assessment-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch {}
  };

  // Build radar data from results
  const radarData = [];
  if (resultMap.ocean) {
    Object.entries(resultMap.ocean.scores || {}).forEach(([k, v]) => {
      if (typeof v === 'number') radarData.push({ dim: k.slice(0, 8), score: Math.min(v, 10) });
    });
  }
  if (resultMap.eq?.scores?.overall) radarData.push({ dim: 'EQ', score: Math.min(resultMap.eq.scores.overall, 10) });
  if (resultMap.kfla?.scores?.overall) radarData.push({ dim: 'KFLA', score: Math.min(resultMap.kfla.scores.overall, 10) });

  return (
    <div className="space-y-5">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Completed', value: completedCount, color: '#16A34A' },
          { label: 'Remaining', value: batteries.length - completedCount, color: '#F5841F' },
          { label: 'Progress', value: `${pct}%`, color: '#0D9488' },
          { label: 'Batteries', value: batteries.length, color: '#8B5CF6' },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="nx-card p-4 text-center">
            <p className="text-3xl font-black" style={{ color: s.color }}>{s.value}</p>
            <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Progress + Radar */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="nx-card p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#0D9488' }}>Battery Progress</p>
          <div className="h-2 rounded-full overflow-hidden mb-4" style={{ background: 'var(--t-border)' }}>
            <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className="h-full rounded-full" style={{ background: 'linear-gradient(90deg, #0D9488, #14B8A6)' }} />
          </div>
          <div className="space-y-2">
            {batteries.map(b => {
              const color = BATTERY_COLORS[b.id] || '#0D9488';
              return (
                <div key={b.id} className="flex items-center gap-2 text-xs">
                  {b.is_complete ? <Check className="w-3.5 h-3.5" style={{ color: '#16A34A' }} /> : <Clock className="w-3.5 h-3.5" style={{ color: 'var(--t-text-muted)' }} />}
                  <span className="flex-1" style={{ color: b.is_complete ? 'var(--t-text-heading)' : 'var(--t-text-muted)' }}>{b.name}</span>
                  <span className="text-[10px] font-bold" style={{ color: b.is_complete ? '#16A34A' : 'var(--t-text-muted)' }}>
                    {b.is_complete ? 'Done' : 'Pending'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {radarData.length >= 3 && (
          <div className="nx-card p-5" data-testid="overview-radar">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#8B5CF6' }}>Profile Snapshot</p>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke="var(--t-border)" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 8 }} />
                  <Radar name="Score" dataKey="score" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.2} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Share Button */}
      {completedCount >= 3 && (
        <div className="flex justify-center">
          <Button onClick={() => { setShowShareCard(true); generateShareLink(); }}
            className="h-10 px-6 text-xs font-bold rounded-xl gap-2"
            style={{ background: 'linear-gradient(135deg, #0D9488, #0F766E)', color: '#fff' }}
            data-testid="share-results-btn">
            <Share2 className="w-4 h-4" /> Share Assessment Results
          </Button>
        </div>
      )}

      {/* Enterprise Share Card Modal */}
      <AnimatePresence>
        {showShareCard && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.8)' }}
            onClick={() => setShowShareCard(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-xl" onClick={e => e.stopPropagation()}>

              {/* THE CARD */}
              <div ref={shareRef} data-testid="share-card"
                style={{
                  background: 'linear-gradient(160deg, #0B1120 0%, #111827 40%, #0F172A 100%)',
                  borderRadius: 20, padding: 40, position: 'relative', overflow: 'hidden',
                  border: '1px solid rgba(13,148,136,0.2)',
                }}>
                {/* Subtle pattern overlay */}
                <div style={{ position: 'absolute', inset: 0, opacity: 0.03,
                  backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                  backgroundSize: '24px 24px' }} />

                {/* Top: Monogram only (2x) */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img src="/kairera-mono.png" alt="K" style={{ width: 72, height: 72 }} crossOrigin="anonymous" />
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, letterSpacing: '0.1em' }}>ASSESSMENT RESULTS</p>
                    <p style={{ fontSize: 10, color: '#4B5563', marginTop: 2 }}>
                      {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>

                {/* User Name */}
                <div style={{ marginBottom: 28 }}>
                  <p style={{ fontSize: 22, fontWeight: 900, color: '#F9FAFB', letterSpacing: '-0.01em' }}>{userName}</p>
                  <p style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>Career Intelligence Assessment Profile</p>
                </div>

                {/* Assessment Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 28 }}>
                  {batteries.filter(b => b.is_complete).map(b => {
                    const color = BATTERY_COLORS[b.id] || '#0D9488';
                    const result = resultMap[b.id];
                    let scoreLabel = '';
                    if (result) {
                      const s = result.scores;
                      if (s.overall) scoreLabel = `${Math.min(s.overall, 10).toFixed(1)}`;
                      else if (s.primary) scoreLabel = s.primary.charAt(0).toUpperCase() + s.primary.slice(1);
                      else if (s.inverted_score != null) scoreLabel = `${Math.min(s.inverted_score, 10).toFixed(1)}`;
                      else if (s.risk_level) scoreLabel = s.risk_level.charAt(0).toUpperCase() + s.risk_level.slice(1);
                      else {
                        const vals = Object.values(s).filter(v => typeof v === 'number');
                        if (vals.length) scoreLabel = `${Math.min(vals.reduce((a, b) => a + b, 0) / vals.length, 10).toFixed(1)}`;
                      }
                    }
                    return (
                      <div key={b.id} style={{
                        background: `${color}10`, border: `1px solid ${color}25`,
                        borderRadius: 12, padding: '14px 8px', textAlign: 'center',
                      }}>
                        <p style={{ fontSize: 9, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {b.name.split('(')[0].trim().split(' ').slice(0, 2).join(' ')}
                        </p>
                        <p style={{ fontSize: 20, fontWeight: 900, color, marginTop: 6 }}>
                          {scoreLabel || 'N/A'}
                        </p>
                        {scoreLabel && !isNaN(scoreLabel) && (
                          <p style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>/10</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
                  <div>
                    <p style={{ fontSize: 10, color: '#4B5563' }}>
                      {completedCount}/{batteries.length} Batteries Complete
                    </p>
                    {includeLink && shareLink && (
                      <p style={{ fontSize: 9, color: '#0D9488', marginTop: 4 }}>
                        View full results: kairera.com/a/{shareLink}
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 9, color: '#374151', fontWeight: 600 }}>Powered by</p>
                    <img src="/kairera-logo.png" alt="Kairera" style={{ height: 28, marginTop: 4, opacity: 0.6 }} crossOrigin="anonymous" />
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="mt-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={includeLink} onChange={e => setIncludeLink(e.target.checked)}
                    className="rounded" data-testid="include-link-checkbox" />
                  <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>
                    Include public link to assessment dashboard
                  </span>
                </label>
                <div className="flex gap-3">
                  <Button onClick={downloadCard}
                    className="flex-1 h-10 text-xs font-bold rounded-lg gap-1.5"
                    style={{ background: '#0D9488', color: '#fff' }}
                    data-testid="download-share-card-btn">
                    <Download className="w-3.5 h-3.5" /> Download Card
                  </Button>
                  {shareLink && (
                    <Button onClick={() => {
                      const url = `${window.location.origin}/public/assessments/${shareLink}`;
                      navigator.clipboard.writeText(url);
                    }}
                      className="h-10 px-4 text-xs font-semibold rounded-lg gap-1.5"
                      style={{ background: 'var(--t-bg-section)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
                      data-testid="copy-link-btn">
                      <Copy className="w-3.5 h-3.5" /> Copy Link
                    </Button>
                  )}
                  <Button onClick={() => setShowShareCard(false)}
                    className="h-10 px-4 text-xs rounded-lg"
                    style={{ background: 'var(--t-bg-section)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}>
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---- ASSESSMENTS TAB (Cards + Filter + Retake + Coming Soon) ----
function AssessmentsTab({ batteries, results, resultMap, onStart, onRetake }) {
  const [filter, setFilter] = useState('All');
  const [drawerBattery, setDrawerBattery] = useState(null);
  const [drawerInsights, setDrawerInsights] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const filtered = filter === 'All' ? batteries : batteries.filter(b => BATTERY_CATEGORIES[b.id] === filter);

  const openDrawer = async (batteryId) => {
    setDrawerBattery(batteryId);
    setDrawerInsights(null);
    setDrawerLoading(true);
    try {
      const res = await kaireraAPI.getAssessmentInsights(batteryId);
      setDrawerInsights(res.data.insights);
    } catch {} finally { setDrawerLoading(false); }
  };

  const db = drawerBattery;
  const dbResult = db ? resultMap[db] : null;
  const dbBattery = db ? batteries.find(b => b.id === db) : null;
  const dbColor = db ? BATTERY_COLORS[db] : '#0D9488';
  const DbIcon = db ? (BATTERY_ICONS[db] || Brain) : Brain;

  return (
    <div className="space-y-5">
      {/* Category Filter */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar" data-testid="category-filter">
        {CATEGORIES.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)}
            className="px-3 py-1.5 rounded-lg text-[10px] font-bold whitespace-nowrap transition-all"
            style={{
              background: filter === cat ? '#0D9488' : 'var(--t-bg-section)',
              color: filter === cat ? '#fff' : 'var(--t-text-muted)',
              border: `1px solid ${filter === cat ? '#0D9488' : 'var(--t-border)'}`,
            }}
            data-testid={`filter-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
            {cat}
          </button>
        ))}
      </div>

      {/* Assessment Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((b, i) => {
          const Icon = BATTERY_ICONS[b.id] || Brain;
          const color = BATTERY_COLORS[b.id] || '#0D9488';
          const result = resultMap[b.id];
          const category = BATTERY_CATEGORIES[b.id] || 'General';

          let scoreDisplay = null;
          if (result) {
            const s = result.scores;
            if (s.overall) scoreDisplay = `${Math.min(s.overall, 10).toFixed(1)}/10`;
            else if (s.primary) scoreDisplay = s.primary;
            else if (s.inverted_score != null) scoreDisplay = `${Math.min(s.inverted_score, 10).toFixed(1)}/10`;
            else if (s.risk_level) scoreDisplay = s.risk_level;
            else {
              const vals = Object.values(s).filter(v => typeof v === 'number');
              if (vals.length) scoreDisplay = `${Math.min(vals.reduce((a, c) => a + c, 0) / vals.length, 10).toFixed(1)}/10`;
            }
          }

          return (
            <motion.div key={b.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="nx-card p-5 flex flex-col group hover:shadow-md transition-all cursor-pointer"
              onClick={() => b.is_complete ? openDrawer(b.id) : onStart(b.id)}
              data-testid={`battery-card-${b.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${color}12` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                  style={{
                    background: b.is_complete ? '#16A34A12' : 'var(--t-bg-section)',
                    color: b.is_complete ? '#16A34A' : 'var(--t-text-muted)',
                    border: `1px solid ${b.is_complete ? '#16A34A20' : 'var(--t-border)'}`,
                  }}>
                  {b.is_complete ? 'Complete' : 'Start'}
                </span>
              </div>
              <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--t-text-heading)' }}>{b.name}</h3>
              <p className="text-[10px] mb-2 line-clamp-2 flex-1" style={{ color: 'var(--t-text-muted)' }}>{b.description}</p>
              <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid var(--t-border)' }}>
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                  style={{ background: `${color}08`, color }}>{category}</span>
                {scoreDisplay && (
                  <span className="text-sm font-black" style={{ color }}>{scoreDisplay}</span>
                )}
                {!b.is_complete && (
                  <span className="text-[10px] font-semibold" style={{ color: 'var(--t-text-muted)' }}>{b.total_items} items</span>
                )}
              </div>
              {/* Retake button */}
              {b.is_complete && (
                <button onClick={(e) => { e.stopPropagation(); onRetake(b.id); }}
                  className="mt-2 flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: `${color}08`, color, border: `1px solid ${color}20` }}
                  data-testid={`retake-${b.id}`}>
                  <RotateCcw className="w-3 h-3" /> Retake Assessment
                </button>
              )}
            </motion.div>
          );
        })}

        {/* Coming Soon Cards */}
        {filter === 'All' && COMING_SOON.map((cs, i) => (
          <motion.div key={cs.name} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (batteries.length + i) * 0.04 }}
            className="nx-card p-5 flex flex-col opacity-60" data-testid={`coming-soon-${cs.name.toLowerCase()}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `${cs.color}08` }}>
                <cs.icon className="w-5 h-5" style={{ color: cs.color }} />
              </div>
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'var(--t-bg-section)', color: 'var(--t-text-muted)', border: '1px solid var(--t-border)' }}>
                Coming Soon
              </span>
            </div>
            <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--t-text-heading)' }}>{cs.name}</h3>
            <p className="text-[10px] flex-1" style={{ color: 'var(--t-text-muted)' }}>{cs.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Side Drawer */}
      <AnimatePresence>
        {drawerBattery && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex justify-end"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setDrawerBattery(null)}>
            <motion.div initial={{ x: 500 }} animate={{ x: 0 }} exit={{ x: 500 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="w-full max-w-lg h-full overflow-y-auto"
              style={{ background: 'var(--t-bg)', borderLeft: '1px solid var(--t-border)' }}
              onClick={e => e.stopPropagation()}
              data-testid={`assessment-drawer-${drawerBattery}`}>
              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${dbColor}12` }}>
                      <DbIcon className="w-5 h-5" style={{ color: dbColor }} />
                    </div>
                    <div>
                      <h2 className="text-lg font-extrabold" style={{ color: 'var(--t-text-heading)' }}>{dbBattery?.name}</h2>
                      <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{dbBattery?.description}</p>
                    </div>
                  </div>
                  <button onClick={() => setDrawerBattery(null)} className="p-1.5 rounded-lg" style={{ background: 'var(--t-bg-section)' }}>
                    <X className="w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
                  </button>
                </div>

                {dbResult && (
                  <div className="nx-card p-4" data-testid="drawer-scores">
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: dbColor }}>Your Scores</p>
                    <ScoreDisplay batteryId={drawerBattery} scores={dbResult.scores} color={dbColor} />
                  </div>
                )}

                {drawerLoading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" style={{ color: dbColor }} />
                    <span className="text-xs" style={{ color: 'var(--t-text-muted)' }}>Generating insights...</span>
                  </div>
                )}

                {drawerInsights && (
                  <>
                    <div className="nx-card p-4" style={{ borderLeft: `4px solid ${dbColor}` }}>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{drawerInsights.summary}</p>
                    </div>
                    {(drawerInsights.key_strengths || []).length > 0 && (
                      <div className="nx-card p-4">
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#16A34A' }}>Key Strengths</p>
                        {drawerInsights.key_strengths.map((s, i) => (
                          <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg mb-1" style={{ background: '#16A34A06' }}>
                            <Zap className="w-3 h-3 flex-shrink-0" style={{ color: '#16A34A' }} />
                            <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{s}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(drawerInsights.improvement_areas || []).length > 0 && (
                      <div className="nx-card p-4">
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#F5841F' }}>Improvement Suggestions</p>
                        {drawerInsights.improvement_areas.map((a, i) => (
                          <div key={i} className="px-3 py-2 rounded-lg mb-1.5" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                            <p className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>{a.area}</p>
                            <p className="text-[11px] mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{a.current_state}</p>
                            <p className="text-[11px] mt-1 font-semibold" style={{ color: '#F5841F' }}>{a.action}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {drawerInsights.career_implications && (
                      <div className="nx-card p-4" style={{ borderLeft: '4px solid #8B5CF6' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-3.5 h-3.5" style={{ color: '#8B5CF6' }} />
                          <span className="text-[10px] font-bold uppercase" style={{ color: '#8B5CF6' }}>Career Impact</span>
                        </div>
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{drawerInsights.career_implications}</p>
                      </div>
                    )}
                    {(drawerInsights.quick_wins || []).length > 0 && (
                      <div className="nx-card p-4">
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#0D9488' }}>Quick Wins</p>
                        {drawerInsights.quick_wins.map((w, i) => (
                          <div key={i} className="flex items-start gap-2 px-2 py-1.5 rounded-lg mb-1" style={{ background: '#0D948806' }}>
                            <Lightbulb className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#0D9488' }} />
                            <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{w}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---- PROFILE TAB (Who You Are) ----
function ProfileTab({ results, completedCount, totalCount }) {
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (completedCount < 3) { setLoading(false); return; }
      try {
        const res = await kaireraAPI.getAssessmentPersona();
        setPersona(res.data.persona);
      } catch {} finally { setLoading(false); }
    })();
  }, [completedCount]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: '#8B5CF6' }} />
      <span className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Synthesizing your personality profile...</span>
    </div>
  );

  if (!persona) return (
    <div className="max-w-md mx-auto text-center py-16">
      <User className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--t-text-muted)' }} />
      <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--t-text-heading)' }}>Not Enough Data</h3>
      <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
        Complete at least 3 assessment batteries to unlock your personality synthesis.
        ({completedCount}/{totalCount} complete)
      </p>
    </div>
  );

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
        className="nx-card p-6 text-center relative overflow-hidden" data-testid="persona-hero">
        <div className="absolute inset-0 opacity-5"
          style={{ background: 'radial-gradient(circle at 50% 0, #8B5CF6, transparent 70%)' }} />
        <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: '#8B5CF6' }} />
        <h2 className="text-2xl font-black" style={{ color: '#8B5CF6' }}>{persona.archetype}</h2>
        <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: 'var(--t-text-heading)' }}>{persona.tagline}</p>
      </motion.div>

      {/* Traits */}
      {(persona.dominant_traits || []).length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-2" data-testid="persona-traits">
          {persona.dominant_traits.map((t, i) => (
            <span key={i} className="px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ background: 'var(--t-bg-section)', color: '#8B5CF6', border: '1px solid #8B5CF620' }}>
              {t}
            </span>
          ))}
        </motion.div>
      )}

      {/* Summary */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className="nx-card p-5" data-testid="persona-summary">
        <p className="text-xs leading-relaxed whitespace-pre-line" style={{ color: 'var(--t-text-secondary)' }}>
          {persona.summary}
        </p>
      </motion.div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { label: 'Leadership Style', value: persona.leadership_style, icon: Award, color: '#F5841F' },
          { label: 'Communication Style', value: persona.communication_style, icon: Users, color: '#3B82F6' },
          { label: 'Ideal Environment', value: persona.work_environment_fit, icon: Target, color: '#16A34A' },
          { label: 'Growth Edge', value: persona.growth_edge, icon: TrendingUp, color: '#8B5CF6' },
        ].map((item, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + i * 0.05 }}
            className="nx-card p-4" style={{ borderLeft: `3px solid ${item.color}` }}>
            <div className="flex items-center gap-2 mb-1.5">
              <item.icon className="w-3.5 h-3.5" style={{ color: item.color }} />
              <span className="text-[10px] font-bold uppercase" style={{ color: item.color }}>{item.label}</span>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{item.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Risk Factors */}
      {(persona.risk_factors || []).length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          className="nx-card p-4" data-testid="persona-risks">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#DC2626' }}>Risk Factors</p>
          {persona.risk_factors.map((r, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg mb-1" style={{ background: '#DC262606' }}>
              <AlertTriangle className="w-3 h-3 flex-shrink-0" style={{ color: '#DC2626' }} />
              <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{r}</span>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}

// ---- COMPARISON TAB ----
function ComparisonTab({ completedCount }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (completedCount < 3) { setLoading(false); return; }
      try {
        const res = await kaireraAPI.getAssessmentComparison();
        setData(res.data.comparison);
      } catch {} finally { setLoading(false); }
    })();
  }, [completedCount]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: '#EC4899' }} />
      <span className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Comparing against similar professionals...</span>
    </div>
  );

  if (!data) return (
    <div className="max-w-md mx-auto text-center py-16">
      <Users className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--t-text-muted)' }} />
      <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--t-text-heading)' }}>Not Enough Data</h3>
      <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
        Complete at least 3 assessments to see how you compare to peers.
      </p>
    </div>
  );

  const batts = data.batteries || [];

  // Build radar data for overlay chart
  const radarData = batts.map(b => ({
    name: (BATTERY_NAMES[b.battery_id] || b.battery_name || '').split('(')[0].trim().split(' ').slice(0, 2).join(' '),
    you: b.user_percentile || 0,
    peers: Math.max(30, 100 - (b.user_percentile || 50)),
  }));

  // Build bar data for horizontal comparison
  const barData = batts.map(b => ({
    name: (BATTERY_NAMES[b.battery_id] || b.battery_name || '').split('(')[0].trim().split(' ').slice(0,2).join(' '),
    you: b.user_percentile || 0,
    avg: Math.max(30, 100 - (b.user_percentile || 50)),
    color: BATTERY_COLORS[b.battery_id] || '#0D9488',
  }));

  const percentile = data.overall_percentile || 0;
  const rank = percentile >= 90 ? 'Top 10%' : percentile >= 75 ? 'Top 25%' : percentile >= 50 ? 'Top Half' : 'Below Average';
  const rankColor = percentile >= 75 ? '#16A34A' : percentile >= 50 ? '#F5841F' : '#DC2626';

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      {/* Hero Stats */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
        className="nx-card p-6" data-testid="comparison-hero">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-4xl font-black" style={{ color: '#EC4899' }}>P{percentile}</p>
            <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>Percentile</p>
          </div>
          <div>
            <p className="text-lg font-black" style={{ color: rankColor }}>{rank}</p>
            <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>Ranking</p>
          </div>
          <div>
            <p className="text-2xl font-black" style={{ color: '#0D9488' }}>{data.sample_size || 0}</p>
            <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>Sample Size</p>
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>{data.peer_group || 'N/A'}</p>
            <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>Peer Group</p>
          </div>
        </div>
      </motion.div>

      {/* Radar + Bar Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Radar Overlay */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="nx-card p-5" data-testid="comparison-radar">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#EC4899' }}>
            You vs Peer Average
          </p>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="65%">
                <PolarGrid stroke="var(--t-border)" />
                <PolarAngleAxis dataKey="name" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
                <Radar name="You" dataKey="you" stroke="#EC4899" fill="#EC4899" fillOpacity={0.2} strokeWidth={2} />
                <Radar name="Peers" dataKey="peers" stroke="#6B7280" fill="#6B7280" fillOpacity={0.08} strokeWidth={1.5} strokeDasharray="4 4" />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Bar Chart */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="nx-card p-5" data-testid="comparison-bar">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#0D9488' }}>
            Battery-by-Battery
          </p>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} layout="vertical" margin={{ left: 5, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <YAxis dataKey="name" type="category" width={70} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <Tooltip contentStyle={{ background: 'var(--t-bg-card)', border: '1px solid var(--t-border)', borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="you" name="You" fill="#EC4899" radius={[0, 4, 4, 0]} barSize={8} />
                <Bar dataKey="avg" name="Peers" fill="#6B7280" radius={[0, 4, 4, 0]} barSize={8} opacity={0.4} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Battery Comparison Cards */}
      <div className="space-y-3">
        {batts.map((b, i) => {
          const color = BATTERY_COLORS[b.battery_id] || '#0D9488';
          const Icon = BATTERY_ICONS[b.battery_id] || Brain;
          const pct = b.user_percentile || 0;
          const badge = pct >= 80 ? 'Elite' : pct >= 60 ? 'Strong' : pct >= 40 ? 'Average' : 'Developing';
          const badgeColor = pct >= 80 ? '#16A34A' : pct >= 60 ? '#3B82F6' : pct >= 40 ? '#F5841F' : '#DC2626';
          return (
            <motion.div key={b.battery_id} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
              className="nx-card p-4 flex items-center gap-4" data-testid={`comparison-${b.battery_id}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${color}12` }}>
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{b.battery_name}</span>
                  <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{ background: `${badgeColor}10`, color: badgeColor }}>{badge}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px]">
                  <span style={{ color: '#16A34A' }}>Standout: {b.standout_factor}</span>
                  <span style={{ color: '#DC2626' }}>Gap: {b.gap_factor}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-lg font-black" style={{ color }}>P{pct}</span>
                <div className="w-16 h-2 rounded-full overflow-hidden" style={{ background: 'var(--t-border)' }}>
                  <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                    transition={{ delay: 0.5 + i * 0.1, duration: 0.8 }}
                    className="h-full rounded-full" style={{ background: color }} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Strengths vs Peers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="nx-card p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#16A34A' }}>Above Peers</p>
          {(data.strengths_vs_peers || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg mb-1" style={{ background: '#16A34A06' }}>
              <Zap className="w-3 h-3 flex-shrink-0" style={{ color: '#16A34A' }} />
              <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{s}</span>
            </div>
          ))}
        </div>
        <div className="nx-card p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#DC2626' }}>Below Peers</p>
          {(data.areas_below_peers || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg mb-1" style={{ background: '#DC262606' }}>
              <AlertTriangle className="w-3 h-3 flex-shrink-0" style={{ color: '#DC2626' }} />
              <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Insight */}
      {data.insight && (
        <div className="nx-card p-4" style={{ borderLeft: '4px solid #EC4899' }}>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-3.5 h-3.5" style={{ color: '#EC4899' }} />
            <span className="text-[10px] font-bold uppercase" style={{ color: '#EC4899' }}>Comparison Insight</span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{data.insight}</p>
        </div>
      )}
    </div>
  );
}

// ---- Score Display Component for Drawer ----
function ScoreDisplay({ batteryId, scores, color }) {
  if (!scores) return null;

  if (batteryId === 'ocean') {
    const barData = Object.entries(scores).map(([k, v]) => ({ name: k, score: Math.min(typeof v === 'number' ? v : 0, 10) }));
    return (
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={barData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} />
            <XAxis dataKey="name" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
            <YAxis domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
            <Tooltip contentStyle={{ background: 'var(--t-bg-card)', border: '1px solid var(--t-border)', borderRadius: 10, fontSize: 11 }} />
            <Bar dataKey="score" fill={color} radius={[6, 6, 0, 0]} barSize={30} animationDuration={1000} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (batteryId === 'eq') {
    const dims = scores.dimensions || {};
    const barData = Object.entries(dims).map(([k, v]) => ({ name: k.slice(0, 8), score: Math.min(v, 10) }));
    return (
      <div>
        <p className="text-xl font-black mb-3" style={{ color }}>{Math.min(scores.overall || 0, 10).toFixed(1)}/10</p>
        <div style={{ height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: 'var(--t-text-muted)', fontSize: 8 }} />
              <YAxis domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
              <Bar dataKey="score" fill={color} radius={[6, 6, 0, 0]} barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (batteryId === 'disc') {
    const DC = { D: '#DC2626', I: '#F5841F', S: '#16A34A', C: '#3B82F6' };
    const DL = { D: 'Dominance', I: 'Influence', S: 'Steadiness', C: 'Conscientiousness' };
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-4xl font-black" style={{ color: DC[scores.primary] }}>{scores.primary}</span>
          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Primary: {DL[scores.primary]}</p>
            <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>Secondary: {DL[scores.secondary] || scores.secondary}</p>
          </div>
        </div>
      </div>
    );
  }

  if (batteryId === 'kfla') {
    const barData = Object.entries(scores.factors || {}).map(([k, v]) => ({ name: k, score: Math.min(v, 10) }));
    return (
      <div>
        <p className="text-xl font-black mb-3" style={{ color }}>{Math.min(scores.overall || 0, 10).toFixed(1)}/10</p>
        <div style={{ height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: 'var(--t-text-muted)', fontSize: 10 }} />
              <YAxis domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
              <Bar dataKey="score" fill={color} radius={[6, 6, 0, 0]} barSize={35} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {Object.entries(scores).filter(([k]) => !['per_derailer', 'dimensions', 'critical_flags'].includes(k)).map(([k, v]) => (
        <div key={k} className="flex items-center justify-between px-3 py-2 rounded-lg"
          style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
          <span className="text-xs font-semibold capitalize" style={{ color: 'var(--t-text-muted)' }}>{k.replace(/_/g, ' ')}</span>
          <span className="text-xs font-bold" style={{ color }}>
            {typeof v === 'number' ? Math.min(v, 10).toFixed(1) : Array.isArray(v) ? v.join(', ') : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}
